import {useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {initialValues} from './_module'
import clsx from 'clsx'
import Loader from 'app/custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import {useAppSelector, useAppDispatch} from '../../../../redux/hooks'
import {createRole, getPermissions, reset} from '../../../../redux/role/roleSlice'

const CreateRole = () => {
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<any[]>([])
  const [permissions, setPermission] = useState([])
  const [filteredPermissions, setFilteredPermissions] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const {t} = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {permission_list, errors} = useAppSelector((state) => state.role)

  useEffect(() => {
    dispatch(getPermissions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPermission(permission_list)
    setFilteredPermissions(permission_list)
    setLoader(false)
  }, [permission_list])

  // Filter permissions based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPermissions(permissions)
    } else {
      const filtered = permissions.filter((p: any) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredPermissions(filtered)
    }
  }, [searchTerm, permissions])

  const userSchema = Yup.object().shape({
    name: Yup.string().required(
      t('VALIDATION.REQUIRED_', {name: t('LABEL.ROLE')}) + ' ' + t('GLOBAL.NAME')
    ),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: userSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      const formData = new FormData()
      formData.append('role_id', '0')
      formData.append('name', values.name)
      selectedCheckboxes.forEach((item: any) => {
        formData.append('permission[]', item)
      })

      // Create New Role
      dispatch(createRole(formData)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          dispatch(reset())
          showSuccessMessage(t('MESSAGES.CREATED'), () => {
            navigate(-1)
          })
        } else if (res.meta.requestStatus === 'rejected') {
          showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        }

        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allCheckboxIds = filteredPermissions.map((data: any) => data.id)
      const combined = [...selectedCheckboxes, ...allCheckboxIds]
      const uniqueIds = combined.filter((id, index) => combined.indexOf(id) === index)
      setSelectedCheckboxes(uniqueIds)
    } else {
      const filteredIds = filteredPermissions.map((data: any) => data.id)
      setSelectedCheckboxes(selectedCheckboxes.filter((id) => !filteredIds.includes(id)))
    }
  }

  type CheckboxChangeEvent = React.ChangeEvent<HTMLInputElement>
  const handleCheckboxChange = (event: CheckboxChangeEvent, recordId: number) => {
    if (event.target.checked) {
      setSelectedCheckboxes([...selectedCheckboxes, recordId])
    } else {
      setSelectedCheckboxes(selectedCheckboxes.filter((id) => id !== recordId))
    }
  }

  return (
    <div className='card mb-5 mb-xl-10 mt-0'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder'>{t('BUTTONS.ADD_', {name: t('LABEL.ROLE')})}</h3>
        </div>
      </div>
      <div className='card-body p-9'>
        {!loader && (
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            autoComplete='off'
            onSubmit={formik.handleSubmit}
          >
            {/* begin::Form row */}

            {/* First row */}
            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='col-lg-6 col-md-6 col-sm-12 mb-5'>
                <label className='col-form-label label fw-bolder required'>{t('LABEL.ROLE')}</label>
                <input
                  type='text'
                  placeholder={t('LABEL.ROLE')}
                  {...formik.getFieldProps('name')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': (formik.touched.name && formik.errors.name) || errors.name,
                    },
                    {
                      'is-valid': formik.touched.name && !formik.errors.name,
                    }
                  )}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block fw-bold'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.name}
                      </span>
                    </div>
                  </div>
                )}
                {errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block fw-bold'>
                      <span role='alert' className='text-danger'>
                        {t('VALIDATION.DUPLICATED RECORD', {
                          name: t('LABELS.NAME', {name: t('ROLES.ROLE')}),
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className='d-flex justify-content-between'>
                <h3 className='text-center'>{t('LABEL.PERMISSIONS')}</h3>
                {permissions.length > 0 && (
                  <label className='col-form-label label fw-bold'>
                    {t('GLOBAL.SELECT_ALL')}:
                    <input
                      className='form-check-input mx-3'
                      type='checkbox'
                      onChange={handleSelectAll}
                      checked={
                        filteredPermissions.length > 0 &&
                        filteredPermissions.every((p: any) => selectedCheckboxes.includes(p.id))
                      }
                    />
                  </label>
                )}
              </div>

              {/* Search Bar */}
              <div className='col-12 mb-5'>
                <div className='input-group input-group-sm'>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder={t('GLOBAL.SEARCH') + '...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    className='btn btn-sm btn-light'
                    type='button'
                    onClick={() => setSearchTerm('')}
                    disabled={!searchTerm}
                  >
                    <i className='fas fa-times'></i>
                  </button>
                </div>
              </div>

              <hr />

              {filteredPermissions.length > 0 ? (
                filteredPermissions.map((p: any) => {
                  return (
                    <div className='form-check form-check-inline col-3 mx-13' key={p.id}>
                      <label className='form-check form-check-inline form-check-solid mx-2 my-1'>
                        <span className='fw-bold ps-2 fs-6'>{p.name}</span>
                        <input
                          {...formik.getFieldProps('permission')}
                          type='checkbox'
                          className='form-check-input mx-3'
                          id={p.id}
                          checked={selectedCheckboxes.includes(p.id)}
                          onChange={(event: CheckboxChangeEvent) =>
                            handleCheckboxChange(event, p.id)
                          }
                        />
                      </label>
                    </div>
                  )
                })
              ) : (
                <div className='col-12 text-center text-muted py-5'>
                  {t('MESSAGES.NO_RESULTS_FOUND')}
                </div>
              )}
            </div>

            {/* Second Row */}
            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='d-flex'>
                <div className='fs-6 fw-bold text-gray-700'>
                  <button
                    type='submit'
                    className='btn btn-sm btn-light btn-active-primary fw-bolder'
                    disabled={
                      formik.isSubmitting ||
                      !formik.isValid ||
                      loading ||
                      selectedCheckboxes.length === 0
                    }
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        <i className='fas fa-save mx-2'></i>
                        {t('BUTTONS.SAVE')}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {t('GLOBAL.LOADING')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  &nbsp;&nbsp;
                  <Link
                    className='btn btn-sm btn-flex btn-light btn-active-dark fw-bolder'
                    to='/roles-list'
                    onClick={() => dispatch(reset())}
                  >
                    <b>
                      <i className='fa-solid fa-circle-left mx-2'></i>
                      {t('BUTTONS.BACK')}
                    </b>
                  </Link>
                  <div />
                </div>
              </div>
            </div>
            {/* begin::Form row */}
          </form>
        )}
        {loader && <Loader />}
      </div>
    </div>
  )
}

export default CreateRole
